import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import {
  FiDollarSign,
  FiUsers,
  FiCalendar,
  FiShield,
  FiActivity,
  FiDatabase,
  FiTrendingUp,
  FiTarget,
  FiFlag,
  FiUserCheck,
  FiBriefcase,
  FiMic,
  FiBarChart2,
} from 'react-icons/fi';
import { DmsIcon, ClientAccountsIcon, TaxIcon, FranIcon } from './components/icons/ServiceIcons';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import DashboardLayout from './components/layout/DashboardLayout';
import HomePage from './features/home/components/HomePage';
import AuthPage from './features/auth/components/AuthPage';
import UserDashboard from './features/dashboard-user/components/UserDashboard';
import AdminDashboard from './features/dashboard-admin/components/AdminDashboard';
import DbViewer from './features/db-viewer/components/DbViewer';
import BookkeepingPage from './features/bookkeeping/components/BookkeepingPage';
import DmsPage from './features/dms/DmsPage';
import FranchiseePage from './features/franchisee/FranchiseePage';
import ProfilePage from './features/profile/ProfilePage';
import SettingsPage from './features/settings/SettingsPage';
import MusicPage from './features/music/MusicPage';
import GamesPage from './features/games/GamesPage';
import PublicPlayPage from './features/games/PublicPlayPage';
import NotesPage from './features/notes/NotesPage';
import PayrollPage from './features/payroll/PayrollPage';
import TranscribePage from './features/transcribe/TranscribePage';
import SchedulingPage from './features/scheduling/SchedulingPage';
import DocViewerPage from './features/bookkeeping/components/DocViewerPage';
import AdvertiserConsolePage from './features/advertiser/AdvertiserConsolePage';
import AdModerationPage from './features/advertiser/AdModerationPage';
import UserApprovalsPage from './features/admin/UserApprovalsPage';
import RolesPermissionsPage from './features/roles/RolesPermissionsPage';
import { User, UserRole, isAdminRole, getRoleLabel, getUserTypeId, ADMIN_ROLES } from './types/user';
import { loadTable, addRow, getTableData, saveTableData, getNextId } from './features/db-viewer/data/dataAccess';
import { saveSession, restoreSession, clearSession, onCrossTabLogout, SessionUser } from './lib/session';
import { userServiceNavDecision } from './lib/serviceAccess';
import { redeemPromo } from './lib/promos';
import './styles/global.css';

// Master switch for plan-based nav gating (layer 2.5). Flip to false to disable
// while iterating. When true, a service nav item is hidden only when the real
// user's plan POSITIVELY excludes it (fail-open otherwise).
const PLAN_NAV_GATING = true;

// CCFOS — sibling educational markets/charting app that lives on its OWN
// sub-domain (default ccfos.akountant.com) and shares Akountant's logins via API.
// Config-driven URL (NOT hard-coded) so the app can be relocated to a standalone
// domain later with no code change — set REACT_APP_CCFOS_URL at build time.
// "CCFOS" = Currency · Commodities · Crypto · Futures · Options · Stocks.
// The link opens in a new window; token/session are NEVER passed in the URL —
// CCFOS establishes its own session against the shared auth API.
const CCFOS_URL = process.env.REACT_APP_CCFOS_URL || 'https://ccfos.akountant.com';

// =============================================
// Sidebar item definitions
// =============================================

// =============================================
// Nav registry — single source of truth for left-bar items.
// Each item declares the roles allowed to see it (visibility layer 1).
// The path slug is prefixed with /admin or /dashboard per the user's role.
// Future: layer in user_rights (per-type service access) + per-user overrides
// so visibility is editable as data, not code (see canSeeNavItem note below).
// =============================================
const ALL_NAV_ROLES: UserRole[] = [...ADMIN_ROLES, 'account_owner', 'franchisee', 'end_user'];
const OWNER_FRAN: UserRole[] = ['account_owner', 'franchisee'];

// serviceName maps a nav item to a user_rights service so admins can toggle its
// visibility per user-type from Data & Backups (status 'No' hides it).
// `external` holds a full URL — when set, the item opens that URL in a new window
// instead of routing to an in-app /dashboard/<slug> page.
interface NavDef { label: string; slug: string; icon: React.ReactNode; roles: UserRole[]; serviceName?: string; external?: string; }

const NAV_ITEMS: NavDef[] = [
  { label: 'Franchisee Ops',      slug: 'franchisee',       icon: <FranIcon />,            roles: ['site_owner', 'site_admin', 'site_dev', 'franchisee'] },
  { label: 'Documents (DMS)',     slug: 'dms',              icon: <DmsIcon />,             roles: ALL_NAV_ROLES, serviceName: 'Cloud Drive (like Prism)' },
  { label: 'Your Accounts',       slug: 'your-accounts',    icon: <FiDollarSign />,        roles: [...ADMIN_ROLES, ...OWNER_FRAN], serviceName: 'Accounting' },
  { label: "Client's Accounts",   slug: 'clients-accounts', icon: <ClientAccountsIcon />,  roles: [...ADMIN_ROLES, ...OWNER_FRAN], serviceName: 'Accounting' },
  { label: 'Payroll',             slug: 'payroll',          icon: <FiBriefcase />,         roles: [...ADMIN_ROLES, ...OWNER_FRAN], serviceName: 'Payroll' },
  { label: 'Scheduling',          slug: 'scheduling',       icon: <FiCalendar />,          roles: ALL_NAV_ROLES },
  { label: 'Tax Filing',          slug: 'tax-filing',       icon: <TaxIcon />,             roles: [...ADMIN_ROLES, ...OWNER_FRAN] },
  { label: 'Transcribe',          slug: 'transcribe',       icon: <FiMic />,               roles: ALL_NAV_ROLES },
  { label: 'CCFOS',               slug: 'ccfos',            icon: <FiBarChart2 />,         roles: ALL_NAV_ROLES, external: CCFOS_URL },
  { label: 'Contacts',            slug: 'contacts',         icon: <FiUsers />,             roles: OWNER_FRAN, serviceName: 'Contacts' },
  { label: 'Calendar',            slug: 'calendar',         icon: <FiCalendar />,          roles: ['account_owner', 'franchisee', 'end_user'], serviceName: 'Calendar' },
  { label: 'Advertise',           slug: 'advertiser',       icon: <FiTarget />,            roles: [...ADMIN_ROLES, ...OWNER_FRAN] },
  // Your Music & Your Photos moved out of the left nav into the service top bar
  // (src/components/layout/ServiceTopBar.tsx). Routes below are unchanged.
  { label: 'Reports & Analytics', slug: 'reports',          icon: <FiTrendingUp />,        roles: [...ADMIN_ROLES] },
  { label: 'Roles & Permissions', slug: 'roles',            icon: <FiShield />,            roles: [...ADMIN_ROLES] },
  { label: 'User Approvals',      slug: 'user-approvals',   icon: <FiUserCheck />,         roles: [...ADMIN_ROLES] },
  { label: 'Ad Moderation',       slug: 'ad-moderation',    icon: <FiFlag />,              roles: [...ADMIN_ROLES] },
  { label: 'Audit Logs',          slug: 'audit',            icon: <FiActivity />,          roles: [...ADMIN_ROLES] },
  { label: 'Data & Backups',      slug: 'data',             icon: <FiDatabase />,          roles: [...ADMIN_ROLES] },
];

// Layer 2 — user_rights override. Returns true only if there's an explicit
// 'No' row for this user-type + service (i.e. an admin turned it off in
// Data & Backups). No row → not denied (fall back to the role allowlist).
function navRightDenied(role: UserRole, serviceName?: string): boolean {
  if (!serviceName) return false;
  const typeId = getUserTypeId(role);
  if (!typeId) return false;
  const row = getTableData('user_rights').find((r: any) =>
    // eslint-disable-next-line eqeqeq
    r.user_type_id == typeId && r.service_name === serviceName && r.is_active != 0
  );
  return !!row && row.status === 'No';
}

// Layer 3 — per-user override. Highest precedence: force show/hide a nav item
// for ONE specific user (user_nav_override table), beating role + user_rights.
function navUserOverride(userId: number | undefined, slug: string): 'show' | 'hide' | null {
  if (!userId) return null;
  const row = getTableData('user_nav_override').find((r: any) =>
    // eslint-disable-next-line eqeqeq
    r.user_id == userId && r.nav_key === slug && r.is_active != 0 && r.is_deleted != 1
  );
  if (!row) return null;
  // eslint-disable-next-line eqeqeq
  return row.is_visible == 1 ? 'show' : 'hide';
}

// The real user's assigned plan (null when unknown / not yet migrated).
function userPlanId(userId?: number): number | null {
  if (!userId) return null;
  const u = getTableData('users').find((r: any) => r.user_id == userId); // eslint-disable-line eqeqeq
  return u?.plan_id == null ? null : Number(u.plan_id); // eslint-disable-line eqeqeq
}

// Layer 2.5 — plan gate (role ∩ plan, the "plan includes it" half). Hide a service
// nav item only when we POSITIVELY know the user's plan excludes it. Fail open
// (no plan / unresolved service / no plan-access data), exempt admins + franchisees
// (unlimited), and naturally skipped in View As (userId is undefined there).
function navPlanDenied(role: UserRole, userId: number | undefined, serviceName?: string): boolean {
  if (!PLAN_NAV_GATING || !serviceName || !userId) return false;
  if (isAdminRole(role) || role === 'franchisee') return false;
  // Plan OR an active per-user grant can include the service; only a positive
  // exclusion (plan has data, doesn't include it, and no grant) hides the item.
  return userServiceNavDecision(userId, userPlanId(userId), serviceName) === 'exclude';
}

// A user is a franchisee by CAPABILITY (owns an active user_franchisee row), not by
// a dedicated user_type. So an Account Owner who later becomes a franchisee keeps
// their base role + clients AND gains the Franchisee Ops view. Same login, both hats.
function userIsFranchisee(userId?: number): boolean {
  if (userId == null) return false;
  // eslint-disable-next-line eqeqeq
  return getTableData('user_franchisee').some((f: any) =>
    f.user_id == userId && f.is_deleted != 1 && f.is_active != 0);
}

// Visibility precedence: layer 3 (per-user override) → layer 1 (role allowlist OR
// franchisee capability) AND layer 2 (user_rights) AND layer 2.5 (plan includes it).
function canSeeNavItem(item: NavDef, role: UserRole, userId?: number): boolean {
  const override = navUserOverride(userId, item.slug);
  if (override === 'show') return true;
  if (override === 'hide') return false;
  const franchiseeCap = item.slug === 'franchisee' && userIsFranchisee(userId);
  if (!item.roles.includes(role) && !franchiseeCap) return false;
  if (navRightDenied(role, item.serviceName)) return false;
  if (navPlanDenied(role, userId, item.serviceName)) return false;
  return true;
}

/** Build the left-nav items for a given role (+ optional user for layer-3 overrides). */
function getSidebarForRole(role: UserRole, userId?: number) {
  const base = isAdminRole(role) ? '/admin' : '/dashboard';
  return NAV_ITEMS
    .filter(item => canSeeNavItem(item, role, userId))
    .map(item => item.external
      ? { label: item.label, path: item.external, icon: item.icon, external: true }
      : { label: item.label, path: `${base}/${item.slug}`, icon: item.icon });
}

/**
 * Return the dashboard title prefix path for a role
 */
function getDashboardPath(role: UserRole): string {
  return isAdminRole(role) ? '/admin' : '/dashboard';
}

// Placeholder component for pages not yet built
const PlaceholderPage: React.FC<{ title: string }> = ({ title }) => (
  <div style={{ padding: '2rem', textAlign: 'center', color: '#5f6368' }}>
    <h2>{title}</h2>
    <p style={{ marginTop: '1rem' }}>This module is coming soon.</p>
  </div>
);

/** Convert SessionUser to User (adds role typing) */
function sessionToUser(s: SessionUser): User {
  return {
    id: s.id,
    firstName: s.firstName,
    lastName: s.lastName,
    email: s.email,
    role: s.role as UserRole,
    createdAt: s.createdAt,
  };
}

const AppContent: React.FC = () => {
  // Restore session from localStorage + cookie on first load
  const [user, setUser] = useState<User | null>(() => {
    const session = restoreSession();
    return session ? sessionToUser(session) : null;
  });
  const [viewAsRole, setViewAsRole] = useState<UserRole | null>(null);
  const [isNewUser, setIsNewUser] = useState(false);
  const [promoNotice, setPromoNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Listen for cross-tab logout (another tab calls clearSession)
  useEffect(() => {
    const cleanup = onCrossTabLogout(() => {
      setUser(null);
      setViewAsRole(null);
      setIsNewUser(false);
      // Don't navigate — let the route guards handle redirect
    });
    return cleanup;
  }, []);

  // Warm the local cache so plan-based nav gating (layer 2.5) has data to act on.
  // canSeeNavItem reads these via getTableData(); without this they're absent for a
  // normal session, so the gate stays fail-open (inert). Best-effort: failures just
  // leave the gate dormant (still safe). Runs on login, signup, and refresh-restore.
  // The tick bump forces one re-render after the (async) load so the sidebar, which
  // is computed from getTableData() during render, reflects the now-warm cache.
  const [, bumpNavRefresh] = useState(0);
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    Promise.all([
      loadTable('users'),                       // for the logged-in user's plan_id
      loadTable('site_services'),
      loadTable('site_service_plan_access'),
      loadTable('user_service_grant'),
      loadTable('user_franchisee'),             // so the Franchisee Ops nav (capability) has data
    ]).then(() => { if (!cancelled) bumpNavRefresh(n => n + 1); })
      .catch(() => { /* gate stays fail-open if this fails */ });
    return () => { cancelled = true; };
  }, [user?.id]);

  // Auto-dismiss the signup promo banner after a few seconds.
  useEffect(() => {
    if (!promoNotice) return;
    const t = setTimeout(() => setPromoNotice(null), 6000);
    return () => clearTimeout(t);
  }, [promoNotice]);

  // The effective role — either the real role or the "view as" role
  const effectiveRole: UserRole | null = viewAsRole || (user?.role ?? null);
  const effectiveIsAdmin = effectiveRole ? isAdminRole(effectiveRole) : false;

  // Handle "View As" changes — navigate to the target role's dashboard
  const handleViewAsChange = (role: UserRole | null) => {
    setViewAsRole(role);
    if (role) {
      navigate(getDashboardPath(role));
    } else if (user) {
      navigate(getDashboardPath(user.role));
    }
  };

  // Login handler — called after email check + password + 2FA
  const handleLogin = async (email: string, password: string) => {
    try {
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Show error to user (could be invalid credentials, locked account, etc.)
        alert(data.message || 'Login failed');
        return;
      }

      const { user: apiUser, authToken } = data;

      // Store auth token for future API calls
      localStorage.setItem('akt_auth_token', authToken);

      const role: UserRole = apiUser.role || 'account_owner';

      const loggedInUser: User = {
        id: apiUser.id,
        firstName: apiUser.firstName || 'User',
        lastName: apiUser.lastName || '',
        email: apiUser.email,
        role,
        createdAt: apiUser.createdAt || new Date().toISOString(),
      };

      // Persist session → survives refresh, works in new tabs
      saveSession({
        id: loggedInUser.id,
        firstName: loggedInUser.firstName,
        lastName: loggedInUser.lastName,
        email: loggedInUser.email,
        role: loggedInUser.role,
        createdAt: loggedInUser.createdAt,
      });
      setUser(loggedInUser);
      setViewAsRole(null);
      setIsNewUser(false);
      navigate(isAdminRole(role) ? '/admin' : '/dashboard');
    } catch (err) {
      console.error('Login error:', err);
      alert('Unable to connect to server. Please try again.');
    }
  };

  // New user handler — create account_owner in users table, go to About Yourself form
  const handleNewUser = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    promoCode?: string
  ) => {
    try {
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      // Create the account through the PUBLIC signup endpoint (creates the users
      // row WITH a real bcrypt password hash and returns an auth token). This is
      // NOT the admin-only /api/db CRUD — a brand-new user has no admin rights.
      const response = await fetch(`${API_BASE}/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ firstName, lastName, email, password, role: 'account_owner' }),
      });

      const data = await response.json();

      if (!response.ok) {
        alert(data.message || 'Sign up failed. Please try again.');
        return;
      }

      const { user: apiUser, authToken } = data;

      // Store the auth token so subsequent API calls are authenticated.
      if (authToken) localStorage.setItem('akt_auth_token', authToken);

      const newUser: User = {
        id: String(apiUser.id),
        firstName: apiUser.firstName || firstName,
        lastName: apiUser.lastName || lastName,
        email: apiUser.email || email,
        role: (apiUser.role as UserRole) || 'account_owner',
        createdAt: apiUser.createdAt || new Date().toISOString(),
      };

      // Persist session → survives refresh, works in new tabs.
      saveSession({
        id: newUser.id,
        firstName: newUser.firstName,
        lastName: newUser.lastName,
        email: newUser.email,
        role: newUser.role,
        createdAt: newUser.createdAt,
      });
      setUser(newUser);
      setViewAsRole(null);
      setIsNewUser(true);

      // Optional promo at signup — redeem against the brand-new user. Non-blocking:
      // a bad code never stops signup, it just reports back.
      if (promoCode) {
        try {
          await Promise.all([loadTable('site_service_promos'), loadTable('user_promo'), loadTable('user_service_grant')]);
          const res = await redeemPromo(promoCode, Number(newUser.id), Number(newUser.id));
          setPromoNotice({ ok: res.ok, text: res.ok ? res.message : `Promo not applied: ${res.message}` });
        } catch (err) {
          console.error('Promo redemption at signup failed:', err);
        }
      }

      // Navigate to Your Accounts > Masters > Family & Businesses (About Yourself form)
      navigate('/dashboard/your-accounts');
    } catch (err) {
      console.error('Signup error:', err);
      alert('Unable to connect to server. Please try again.');
    }
  };

  const handleLogout = useCallback(() => {
    clearSession();  // clears localStorage + cookie → triggers cross-tab logout
    setUser(null);
    setViewAsRole(null);
    setIsNewUser(false);
    navigate('/');
  }, [navigate]);

  const isAuthenticated = user !== null;

  // Determine which sidebar to show based on effective role
  // Per-user nav overrides apply to the real logged-in user, not during View As (role preview).
  const navUserId = !viewAsRole && user ? Number(user.id) : undefined;
  const currentSidebarItems = effectiveRole ? getSidebarForRole(effectiveRole, navUserId) : [];

  // Build page title from current path
  const getPageTitle = (basePath: string): string => {
    const path = location.pathname;
    if (path === basePath) return effectiveIsAdmin ? 'Admin Dashboard' : 'Dashboard';
    if (path.includes('franchisee')) return 'Franchisee Operations';
    if (path.includes('your-accounts')) return 'Your Accounts';
    if (path.includes('clients-accounts')) return "Client's Accounts";
    if (path.includes('clients')) return 'Client Management';
    if (path.includes('dms')) return 'Document Management System';
    if (path.includes('bookkeeping')) return 'Your Accounts';
    if (path.includes('tax')) return 'Tax Filing';
    if (path.includes('regions')) return 'Regions & Tax Rules';
    if (path.includes('reports')) return 'Reports & Analytics';
    if (path.includes('roles')) return 'Roles & Permissions';
    if (path.includes('audit')) return 'Audit Logs';
    if (path.includes('data')) return 'Data & Backups';
    if (path.includes('transcribe')) return 'Transcribe';
    if (path.includes('contacts')) return 'Contacts';
    if (path.includes('calendar')) return 'Calendar';
    if (path.includes('vault') || path.includes('notes')) return 'Your Knowledge Vault (YKV)';
    return 'Settings';
  };

  // Determine which dashboard component to show for the index route
  const DashboardIndex = effectiveIsAdmin ? AdminDashboard : UserDashboard;

  return (
    <div className="app">
      {promoNotice && (
        <div
          role="status"
          style={{
            position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 1000,
            display: 'flex', alignItems: 'center', gap: 10, maxWidth: 'min(92vw, 460px)',
            padding: '10px 14px', borderRadius: 10, fontSize: 14, lineHeight: 1.4,
            color: promoNotice.ok ? '#065f46' : '#9a3412',
            background: promoNotice.ok ? '#ecfdf5' : '#fff7ed',
            border: `1px solid ${promoNotice.ok ? '#a7f3d0' : '#fed7aa'}`,
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          }}
        >
          <span style={{ flex: 1 }}>{promoNotice.ok ? '🎉 ' : ''}{promoNotice.text}</span>
          <button
            onClick={() => setPromoNotice(null)}
            aria-label="Dismiss"
            style={{ border: 'none', background: 'transparent', color: 'inherit', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 2 }}
          >×</button>
        </div>
      )}
      <Routes>
        {/* Public pages with Navbar + Footer */}
        <Route
          path="/"
          element={
            <>
              <Navbar
                isAuthenticated={isAuthenticated}
                onLogout={handleLogout}
                userName={user ? user.firstName : undefined}
              />
              <HomePage />
              <Footer />
            </>
          }
        />

        {/* Unified Auth Page (Log In / Sign Up) */}
        <Route
          path="/auth"
          element={
            isAuthenticated ? (
              <Navigate to={isAdminRole(user!.role) ? '/admin' : '/dashboard'} />
            ) : (
              <>
                <Navbar
                  isAuthenticated={false}
                  onLogout={handleLogout}
                />
                <AuthPage onLogin={handleLogin} onNewUser={handleNewUser} />
                <Footer />
              </>
            )
          }
        />

        {/* Legacy routes — redirect to /auth */}
        <Route path="/login" element={<Navigate to="/auth" />} />
        <Route path="/signup" element={<Navigate to="/auth" />} />

        {/* Public, no-login arcade — where non-members land from a challenge link */}
        <Route path="/play" element={<PublicPlayPage />} />

        {/* Standalone PDF viewer (opened in a new tab from Process Docs/Receipts) */}
        <Route path="/doc-view" element={<DocViewerPage />} />

        {/* End User Dashboard */}
        <Route
          path="/dashboard/*"
          element={
            !isAuthenticated ? (
              <Navigate to="/auth" />
            ) : (
              <DashboardLayout
                sidebarItems={currentSidebarItems}
                title={getPageTitle('/dashboard')}
                userName={`${user!.firstName} ${user!.lastName}`.trim() || user!.email}
                userRole={user!.role}
                isAdmin={false}
                onLogout={handleLogout}
                viewAsRole={viewAsRole}
                onViewAsChange={handleViewAsChange}
              >
                <Routes>
                  <Route index element={<DashboardIndex />} />
                  <Route path="dms" element={<DmsPage />} />
                  <Route path="franchisee" element={<FranchiseePage />} />
                  <Route path="your-accounts" element={<BookkeepingPage scope="self" isNewUser={isNewUser} />} />
                  <Route path="clients-accounts" element={<BookkeepingPage scope="clients" />} />
                  <Route path="payroll" element={<PayrollPage />} />
                  <Route path="scheduling" element={<SchedulingPage />} />
                  <Route path="transcribe" element={<TranscribePage />} />
                  <Route path="bookkeeping" element={<Navigate to="/dashboard/your-accounts" replace />} />
                  <Route path="profile" element={<ProfilePage />} />
                  <Route path="tax-filing" element={<PlaceholderPage title="Tax Filing" />} />
                  <Route path="contacts" element={<PlaceholderPage title="Contacts" />} />
                  <Route path="calendar" element={<PlaceholderPage title="Calendar" />} />
                  <Route path="music" element={<MusicPage />} />
                  <Route path="games" element={<GamesPage />} />
                  <Route path="vault" element={<NotesPage />} />
                  <Route path="notes" element={<Navigate to="../vault" replace />} />
                  <Route path="photos" element={<PlaceholderPage title="Your Photos" />} />
                  <Route path="advertiser" element={<AdvertiserConsolePage />} />
                  <Route path="settings" element={<SettingsPage />} />
                </Routes>
              </DashboardLayout>
            )
          }
        />

        {/* Admin Dashboard */}
        <Route
          path="/admin/*"
          element={
            !isAuthenticated ? (
              <Navigate to="/auth" />
            ) : !isAdminRole(user!.role) ? (
              <Navigate to="/dashboard" />
            ) : (
              <DashboardLayout
                sidebarItems={currentSidebarItems}
                isAdmin={true}
                title={getPageTitle('/admin')}
                userName={`${user!.firstName} ${user!.lastName}`.trim() || user!.email}
                userRole={user!.role}
                onLogout={handleLogout}
                viewAsRole={viewAsRole}
                onViewAsChange={handleViewAsChange}
              >
                <Routes>
                  <Route index element={<DashboardIndex />} />
                  <Route path="clients" element={<PlaceholderPage title="Client Management" />} />
                  <Route path="dms" element={<DmsPage />} />
                  <Route path="franchisee" element={<FranchiseePage />} />
                  <Route path="your-accounts" element={<BookkeepingPage scope="self" />} />
                  <Route path="clients-accounts" element={<BookkeepingPage scope="clients" />} />
                  <Route path="payroll" element={<PayrollPage />} />
                  <Route path="scheduling" element={<SchedulingPage />} />
                  <Route path="transcribe" element={<TranscribePage />} />
                  <Route path="bookkeeping" element={<Navigate to="/admin/your-accounts" replace />} />
                  <Route path="profile" element={<ProfilePage />} />
                  <Route path="tax-filing" element={<PlaceholderPage title="Tax Filing" />} />
                  <Route path="music" element={<MusicPage />} />
                  <Route path="games" element={<GamesPage />} />
                  <Route path="vault" element={<NotesPage />} />
                  <Route path="notes" element={<Navigate to="../vault" replace />} />
                  <Route path="photos" element={<PlaceholderPage title="Your Photos" />} />
                  <Route path="advertiser" element={<AdvertiserConsolePage />} />
                  <Route path="regions" element={<PlaceholderPage title="Regions & Tax Rules" />} />
                  <Route path="reports" element={<PlaceholderPage title="Reports & Analytics" />} />
                  <Route path="roles" element={<RolesPermissionsPage />} />
                  <Route path="ad-moderation" element={<AdModerationPage />} />
                  <Route path="user-approvals" element={<UserApprovalsPage />} />
                  <Route path="audit" element={<PlaceholderPage title="Audit Logs" />} />
                  <Route path="data" element={<DbViewer />} />
                  <Route path="settings" element={<SettingsPage />} />
                </Routes>
              </DashboardLayout>
            )
          }
        />

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </div>
  );
};

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
